import TextInput from "../../components/TextInput/TextInput";
import { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { QRCodeSVG } from "qrcode.react";

import clsx from "clsx";
import BankCard from "../../components/BankCard/BankCard";

import { useTranslation } from "react-i18next";
import ColorPicker from "../../components/ColorPicker/ColorPicker";
import {
  CURRENCY_SELECT_OPTIONS,
  getRandomRgbaColor,
  objectHasChanges,
  rgbaObjectToString,
  showNotification,
} from "../../helpers/utils";
import TwoSidebars from "../../layouts/TwoSidebars/TwoSidebars";
import PageTitle from "../../components/PageTitle/PageTitle";
import ButtonGroup from "../../components/ButtonGroup/ButtonGroup";
import {
  MdCheck,
  MdClose,
  MdDelete,
  MdShare,
  MdSettings,
  MdOutlineNavigateBefore,
  MdOutlineNavigateNext,
  MdInfo,
  MdWarning,
} from "react-icons/md";
import { BUTTON_STYLES, ICON_SIZES } from "../../helpers/constants";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import accountsStore from "../../store/accountsStore";
import SettingsBlocks from "../../components/SettingsBlocks/SettingsBlocks";
import Modal from "../../components/Modal/Modal";
import InfoBlock from "../../components/InfoBlock/InfoBlock";
import useCurrentUser from "../../hooks/useCurrentUser";
import { findIndex } from "lodash-es";

import useMobile from "../../hooks/useMobile";

import useMainFilter from "../../hooks/useMainFilter";
import useGeneralOptionsDropdownMenu from "../../hooks/useGeneralOptionsDropdownMenu";
import { AnimatePresence, motion } from "framer-motion";
import CustomSelect from "src/components/Select/CustomSelect";
import SlideDownUpAnimation from "src/components/Animations/SlideDownUpAnimation";
import ChangesPendingToSave from "src/components/ChangesPendingToSave/ChangesPendingToSave";
import { EButtonKind } from "src/components/Button/ButtonTypes";
import LoadingWithTextPage from "src/components/LoadingWithText/LoadingWithTextPage";

interface INewAccountPage {
  onClose?: () => void;
  onCreated?: (e: any) => void;
  editMode?: boolean;
}

const NewAccountPage: React.FC<INewAccountPage> = ({
  onClose = () => {},
  onCreated = () => {},
  editMode = false,
}) => {
  const location = useLocation();
  const animationDirection = location.state?.animationDirection || "right";
  const { accountId = "" } = useParams();
  const { mainFilter, setMainFilter } = useMainFilter();

  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isRegistering, setIsRegistering] = useState<boolean>(false);

  const [name, setName] = useState<string>("");
  const [bank, setBank] = useState<string>("");
  const [number, setNumber] = useState<string>("");
  const [currency, setCurrency] = useState<string>("");
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [textColor, setTextColor] = useState<any>(getRandomRgbaColor(1));
  const [bgColor, setBgColor] = useState<any>(getRandomRgbaColor(1));
  const [settings, setSettings] = useState<any>({});
  const [showQrCode, setShowQrCode] = useState<boolean>(false);
  const [accountLoaded, setAccountLoaded] = useState<boolean>(!accountId);
  const generalOptionsDropdown = useGeneralOptionsDropdownMenu();

  const { getAccountById, resetCache, accounts, isLoadingAccounts } =
    useCurrentUser();

  const { t } = useTranslation();

  const [showSaveConfirmDialog, setShowSaveConfirmDialog] =
    useState<boolean>(false);
  const [showUpdateConfirmDialog, setShowUpdateConfirmDialog] =
    useState<boolean>(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] =
    useState<boolean>(false);
  const [loadedAccountData, setLoadedAccountData] = useState<any>(null);

  const { registerNewAccount, updateExistingAccount, deleteAccount } =
    accountsStore();

  const accountNavigation = () => {
    const cats = accounts?.data?.data || [];

    const currentIndex = findIndex(cats, {
      account: accountId,
    });

    if (currentIndex !== -1) {
      const nextAccount = cats?.[currentIndex + 1] || null;
      const prevAccount = cats?.[currentIndex - 1] || null;

      return {
        nextAccount,
        prevAccount,
      };
    }

    return { nextAccount: null, prevAccount: null };
  };

  const loadAccount = useCallback(() => {
    if (editMode) {
      const account = getAccountById(accountId);

      if (account) {
        const { bank, currency, name, number, settings } = account;

        setBank(bank);
        setCurrency(currency);
        setName(name);
        setNumber(number);
        setSettings(settings);
        setTextColor(settings?.color);
        setBgColor(settings?.bg);
        setIsDisabled(!!settings?.disabled);
        setLoadedAccountData(account);
        setAccountLoaded(true);
      } else {
      }
    }
  }, [editMode, accountId, getAccountById]);

  useEffect(() => {
    if (accountId && !isLoadingAccounts && editMode) {
      loadAccount();
    }
  }, [accountId, isLoadingAccounts, editMode]);

  const isPerformingAction = [isUpdating, isDeleting, isRegistering].includes(
    true
  );

  const isLoadingSomething = useMemo(
    () =>
      [editMode ? !accountLoaded : false, !!isLoadingAccounts].includes(true),
    [editMode, isLoadingAccounts, accountLoaded]
  );

  const navigate = useNavigate();
  const { isMobile } = useMobile();

  const hasChanges = useMemo(() => {
    if (editMode && loadedAccountData) {
      return objectHasChanges({
        original: {
          name: loadedAccountData?.name,
          bank: loadedAccountData?.bank,
          number: loadedAccountData?.number,
          currency: loadedAccountData?.currency,
          disabled: loadedAccountData?.settings?.disabled,
          color: loadedAccountData?.settings?.color,
          bg: loadedAccountData?.settings?.bg,
        },
        edited: {
          name,
          bank,
          number,
          currency,
          disabled: isDisabled,
          color: textColor,
          bg: bgColor,
        },
      });
    }

    return false;
  }, [name, bank, number, currency, isDisabled, textColor, bgColor]);

  return (
    <>
      <TwoSidebars
        right={
          isLoadingSomething ? null : isMobile ? null : (
            <SettingsBlocks layout="vertical" columns={isMobile ? 2 : 1} />
          )
        }
        isLoading={isLoadingSomething}
      >
        <div
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "gap-2",
            "md:gap-2",
            "h-full",
          ])}
        >
          <AnimatePresence>
            {isLoadingSomething ? (
              <LoadingWithTextPage />
            ) : (
              <>
                <PageTitle
                  icon={<MdSettings size={ICON_SIZES.MD} />}
                  title={t("settings.title")}
                  titleLink="/settings"
                  subtitle={(() => {
                    const options = [
                      {
                        text: "Cuentas",
                        link: "/settings/accounts",
                      },
                    ];

                    if (editMode) {
                      options.push({
                        text: "Editar",
                        link: "",
                      });
                    } else {
                      options.push({
                        text: "Nueva",
                        link: "",
                      });
                    }

                    return options;
                  })()}
                  isLoading={false}
                  rightSide={
                    <>
                      <ButtonGroup
                        align="right"
                        gap={1}
                        buttons={[
                          {
                            icon: (
                              <MdOutlineNavigateBefore size={ICON_SIZES.SM} />
                            ),
                            className: isMobile ? BUTTON_STYLES.HEADING : "",
                            tooltip: "Atras",
                            onClick: () => {
                              navigate("/settings/accounts");
                            },
                          },
                          generalOptionsDropdown,
                        ]}
                      />
                    </>
                  }
                />
                <div className={clsx(["flex", "flex-col", "gap-4", "w-full"])}>
                  <motion.div
                    className={clsx(["flex", "w-full", "flex-col", "relative"])}
                    key={accountId}
                    initial={{
                      opacity: 0,
                      x: animationDirection === "left" ? -300 : 300,
                    }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{
                      opacity: 0,
                      x: animationDirection === "left" ? 300 : -300,
                    }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    <BankCard
                      account=""
                      number={number}
                      owner={name}
                      bank={bank}
                      name={name}
                      variant="custom"
                      customStyles={{
                        background: bgColor
                          ? rgbaObjectToString({ ...bgColor, a: 1 })
                          : {},
                        color: textColor
                          ? rgbaObjectToString({ ...textColor, a: 1 })
                          : {},
                      }}
                      currency={currency}
                      actions={[
                        {
                          hide: [!!number, !!bank].includes(false),
                          icon: <MdShare size={ICON_SIZES.MD} />,
                          onClick: async (e: any) => {
                            e?.preventDefault();
                            await navigator
                              .share({
                                text: [
                                  name || "Cuenta sin nombre",
                                  `Banco: ${bank || "Desconocido"}`,
                                  `Número de cuenta: ${
                                    number || "Desconocido"
                                  }`,
                                  `Moneda: ${currency || "Desconocida"}`,
                                ].join("\n"),
                              })
                              .then((a) => {})
                              .catch((e) => {});
                          },
                          tooltip: t("shared.share"),
                          className: clsx([
                            "!bg-transparent",
                            "!border-0",
                            "!text-current",
                            "!shadow-none",
                          ]),
                          disabled: !number,
                        },
                      ]}
                    />
                  </motion.div>
                  {editMode && (
                    <div
                      className={clsx([
                        "flex",
                        "flex-row",
                        "gap-2",
                        "justify-between",
                        "items-center",
                      ])}
                    >
                      <div
                        className={clsx([
                          "flex",
                          "flex-row",
                          "items-center",
                          "gap-1",
                          "text-xs",
                          "md:text-sm",
                          "font-medium",
                        ])}
                      >
                        {loadedAccountData?.settings?.disabled ? (
                          <MdWarning
                            size={ICON_SIZES.SM}
                            className="text-red-500 dark:text-purple-400"
                          />
                        ) : (
                          <MdInfo
                            size={ICON_SIZES.SM}
                            className="text-blue-400 dark:text-cyan-400"
                          />
                        )}
                        Esta cuenta está{" "}
                        {loadedAccountData?.settings?.disabled
                          ? "deshabilitada"
                          : "habilitada"}
                      </div>
                      <ButtonGroup
                        buttons={[
                          {
                            icon: <MdOutlineNavigateBefore size={32} />,
                            onClick: () => {
                              navigate(
                                `/settings/accounts/${
                                  accountNavigation().prevAccount?.account
                                }`,
                                {
                                  replace: true,
                                  state: {
                                    animationDirection: "left",
                                  },
                                }
                              );
                            },
                            tooltip: "Anterior",
                            disabled:
                              !accountNavigation()?.prevAccount || isUpdating,
                          },
                          {
                            icon: <MdOutlineNavigateNext size={32} />,
                            onClick: () => {
                              navigate(
                                `/settings/accounts/${
                                  accountNavigation().nextAccount?.account
                                }`,
                                {
                                  replace: true,
                                  state: {
                                    animationDirection: "right",
                                  },
                                }
                              );
                            },
                            tooltip: "Siguiente",
                            disabled:
                              !accountNavigation()?.nextAccount || isUpdating,
                          },
                        ]}
                      />
                    </div>
                  )}

                  <SlideDownUpAnimation show={[!number, !bank].includes(true)}>
                    <InfoBlock
                      displayMobileIcon
                      title="Compartir cuenta"
                      message="Para habilitar el botón de compartir debes completar los campos Número de cuenta y Banco."
                    />
                  </SlideDownUpAnimation>
                  <div
                    className={clsx([
                      "grid",
                      "grid-cols-1",
                      "md:grid-cols-2",
                      "gap-2",
                    ])}
                  >
                    <ColorPicker
                      onChange={(color: any) => {
                        setBgColor(color.rgb);
                      }}
                      textColor={textColor}
                      color={bgColor}
                      label={t("accounts_page.bg_color_label")}
                      showText={false}
                    />
                    <ColorPicker
                      onChange={(color: any) => {
                        setTextColor(color.rgb);
                      }}
                      textColor={bgColor}
                      color={textColor}
                      label={t("accounts_page.text_color_label")}
                      showText={false}
                    />
                  </div>
                  <div className="flex flex-col gap-4 md:min-w-[350px]">
                    <div className="flex flex-col gap-4">
                      <div
                        className={clsx([
                          "grid",
                          "grid-cols-1",
                          "md:grid-cols-2",
                          "gap-2",
                        ])}
                      >
                        <TextInput
                          placeholder={t(
                            "accounts_page.short_name_placeholder"
                          )}
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          label={t("accounts_page.short_name_label")}
                          type="text"
                          required
                          disabled={isPerformingAction}
                          autoComplete="name nickname"
                        />
                        <TextInput
                          placeholder={t(
                            "accounts_page.account_number_placeholder"
                          )}
                          value={number}
                          onChange={(e) => setNumber(e.target.value)}
                          label={t("accounts_page.account_number_label")}
                          type="text"
                          disabled={isPerformingAction}
                        />
                      </div>
                      <div
                        className={clsx([
                          "grid",
                          "grid-cols-1",
                          "md:grid-cols-2",
                          "gap-2",
                        ])}
                      >
                        <TextInput
                          placeholder={t("accounts_page.bank_placeholder")}
                          value={bank}
                          onChange={(e) => setBank(e.target.value)}
                          label={t("accounts_page.bank_label")}
                          type="text"
                          disabled={isPerformingAction}
                        />
                        <CustomSelect
                          required
                          label={t("accounts_page.currency_label")}
                          placeholder={t("accounts_page.currency_placeholder")}
                          options={CURRENCY_SELECT_OPTIONS}
                          value={currency}
                          onChange={(e: any) => setCurrency(e?.value)}
                          disabled={isPerformingAction}
                          useValueAsLabel={false}
                          allowSearchModal
                        />
                      </div>
                      <div>
                        <CustomSelect
                          required
                          label={t("Estado")}
                          placeholder={t("Selecciona el estado de tu cuenta")}
                          isSearchable={false}
                          options={[
                            { label: "Deshabilitada", value: "true" },
                            { label: "Habilitada", value: "false" },
                          ]}
                          value={`${isDisabled}`}
                          onChange={(e: any) =>
                            setIsDisabled(e?.value === "true")
                          }
                          disabled={isPerformingAction}
                          useValueAsLabel={false}
                          isClearable={false}
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className={clsx([
                      "flex",
                      "gap-4",
                      "flex-row",
                      "justify-between",
                    ])}
                  >
                    <ChangesPendingToSave show={hasChanges} />

                    <div className={clsx(["flex", "w-full", "justify-end"])}>
                      <ButtonGroup
                        buttons={[
                          {
                            tooltip: t("shared.delete"),
                            onClick: () => {
                              setShowDeleteConfirmDialog(true);
                            },
                            icon: <MdDelete size={ICON_SIZES.MD} />,
                            kind: EButtonKind.DANGER,
                            type: "button",
                            hide: !editMode,
                          },
                          {
                            text: t("shared.update"),
                            onClick: () => {
                              if (editMode) {
                                setShowUpdateConfirmDialog(true);
                              } else {
                                setShowSaveConfirmDialog(true);
                              }
                            },
                            icon: <MdCheck size={ICON_SIZES.MD} />,
                            kind: EButtonKind.SAVE,

                            disabled: !hasChanges,
                            hide: !editMode,
                          },
                          {
                            text: t("shared.save"),
                            onClick: () => setShowSaveConfirmDialog(true),
                            icon: <MdCheck size={ICON_SIZES.MD} />,
                            kind: EButtonKind.SAVE,
                            hide: editMode,
                          },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </AnimatePresence>
        </div>
      </TwoSidebars>

      {showSaveConfirmDialog && (
        <ConfirmDialog
          description={t("dialogs.accounts.save_dialog_question")}
          title={t("dialogs.accounts.save_dialog_title")}
          onClose={() => {
            if (!isRegistering) {
              setShowSaveConfirmDialog(false);
            }
          }}
          onConfirm={async () => {
            setIsRegistering(true);
            const accountId = uuidv4();

            const response = await registerNewAccount({
              account: accountId,
              name,
              bank,
              number,
              currency,
              settings: {
                bg: bgColor,
                color: textColor,
                disabled: isDisabled,
              },
            });

            await resetCache();

            if (response.success) {
              setShowSaveConfirmDialog(false);
              onClose();
              showNotification({
                message: "Cuenta agregada correctamente",
                kind: "success",
              });
              navigate(`/settings/accounts/${accountId}`, { replace: true });
            } else {
              showNotification({
                message: "Error agregando la nueva cuenta",
                kind: "error",
              });
            }

            setIsRegistering(false);
          }}
          confirmKind={EButtonKind.SAVE}
          isLoading={isRegistering}
        />
      )}

      {showUpdateConfirmDialog && (
        <ConfirmDialog
          description={t("dialogs.accounts.update_dialog_question")}
          title={t("dialogs.accounts.update_dialog_title")}
          onClose={() => {
            if (!isUpdating) {
              setShowUpdateConfirmDialog(false);
            }
          }}
          onConfirm={async () => {
            setIsUpdating(true);

            const res = await updateExistingAccount({
              account: accountId,
              name,
              bank,
              number,
              currency,
              settings: {
                ...settings,
                bg: bgColor,
                color: textColor,
                disabled: isDisabled,
              },
            });
            if (isDisabled && mainFilter?.account === accountId) {
              setMainFilter({ account: "" });
            }
            await resetCache();

            if (res.success) {
              setShowUpdateConfirmDialog(false);
              onClose();
              showNotification({
                message: "Cuenta actualizada correctamente",
                kind: "success",
              });
            } else {
              showNotification({
                message: "Error actualizando la cuenta",
                kind: "error",
              });
            }

            setIsUpdating(false);
          }}
          confirmKind={EButtonKind.SAVE}
          isLoading={isUpdating}
        />
      )}

      {showDeleteConfirmDialog && (
        <ConfirmDialog
          description={t("dialogs.accounts.delete_dialog_question")}
          title={t("dialogs.accounts.delete_dialog_title")}
          onClose={() => {
            if (!isDeleting) {
              setShowDeleteConfirmDialog(false);
            }
          }}
          onConfirm={async () => {
            setIsDeleting(true);

            const res = await deleteAccount({ account: accountId });

            if (res.success) {
              setShowDeleteConfirmDialog(false);
              onClose();
              showNotification({
                message: "Cuenta eliminada correctamente",
                kind: "success",
              });
              navigate("/settings/accounts/", { replace: true });
            } else {
              showNotification({
                message: "Error eliminando la cuenta",
                kind: "error",
              });
            }

            setIsDeleting(false);
          }}
          confirmKind={EButtonKind.DANGER}
          isLoading={isDeleting}
        />
      )}

      {showQrCode && (
        <Modal onEscape={() => setShowQrCode(false)}>
          <Modal.Header title="Scan" onClose={() => setShowQrCode(false)} />
          <Modal.Body>
            <div className={clsx(["flex", "items-center", "justify-center"])}>
              <QRCodeSVG
                value={[
                  `Banco: ${bank || "Desconocido"}`,
                  `Número de cuenta: ${number || "Desconocido"}`,
                  `Moneda: ${currency || "Desconocida"}`,
                ].join("\n")}
                title={"eXpenser"}
                size={256}
                bgColor={"#ffffff"}
                fgColor={"#000000"}
                level={"L"}
                type="text"
              />
            </div>
          </Modal.Body>
          <Modal.Footer
            actions={[
              {
                icon: <MdClose size={ICON_SIZES.SM} />,
                onClick: () => setShowQrCode(false),
                text: t("shared.close"),
              },
            ]}
          />
        </Modal>
      )}
    </>
  );
};

export default NewAccountPage;
